import React, { useEffect, useState, useRef } from 'react';
// @ts-ignore
import { TogglePanel } from '@els/els-react--toggle-panel';
// @ts-ignore
import { TextInput } from '@els/els-react--text-input';
// @ts-ignore
import { RadioButtonGroup } from '@els/els-react--radio-button';
// @ts-ignore
import { Pill } from '@els/els-react--pill';
import {
    NumberNotDisplayTitles,
    HSearchFilters,
    HSearchFilterDisplayName,
    AgeFilterTitles,
    HSearchRadioButtonTypeName,
    getFacetValueFromFacetData,
    defaultVisibleItems,
} from '../../../../constants/h-search';
import NoSearchResults from '../../../../components/no-search-results/no-search-results';
import './card-filter-panel.scss';
import AriaLiveAnnouncer from '../../../../components/aria-live-announcer/aria-live-announcer';

const CardFilterPanel = (props: any) => {
    const {
        baseClassName,
        onHandleChangeOption,
        facetData,
        selectedOptionContentType,
        isToggleOpen,
        onClickShowMoreLess,
        currentFilters,
        setSelectedRadioOption,
        selectedRadioOption,
    } = props;

    const radiogroupRef = useRef<HTMLDivElement>(null);
    const facetName = facetData?.facetName;
    const facetValueRows = facetData?.facetValues?.length || '0';
    const [isShowMoreOpen, setShowMoreOpen] = useState(false);
    const [titleShowMoreLess, setTitleShowMoreLess] = useState(
        `Show More (${facetValueRows - defaultVisibleItems})`
    );
    const [isFilterApplied, setFilterApplied] = useState(false);
    const [facetValueData, setFacetValueData] = useState<
        { name: string; count: number }[]
    >([]);
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [filterText, setFiltertext] = useState('');
    const [shouldFocusSixthItem, setShouldFocusSixthItem] = useState(false);
    const [shouldFocusFirstItem, setShouldFocusFirstItem] = useState(false);
    const [announcementMessage, setAnnouncementMessage] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const trim = (name: string) => name.split(':')[1]?.replace(/"/g, '').trim();

    // Flag to check if this is SPECIALITIES or CATEGORIES filter
    const isSpecialitiesFilter = facetName === HSearchFilters.SPECIALITIES;
    const isCategoriesFilter = facetName === HSearchFilters.CATEGORIES;
    const isOrderPreservedFilter = isSpecialitiesFilter || isCategoriesFilter;

    // Helper function to get the selected radio option name
    const getSelectedRadioOptionName = (name: string) =>
        HSearchRadioButtonTypeName[
            name as keyof typeof HSearchRadioButtonTypeName
        ] || name;

    // Helper function to get the selected value for a given facet name
    const getSelectedValue = (name: string) => {
        const selectedOptionRadioName = getSelectedRadioOptionName(name);
        return selectedRadioOption?.[selectedOptionRadioName];
    };

    // Helper function to sort facet values alphabetically by name
    const sortFacetValuesAlphabetically = (facetValues: any[]) => {
        if (!facetValues || facetValues.length === 0) {
            return facetValues;
        }
        return [...facetValues].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
    };

    const handleSelectedRadioOption = (name: any, value: any) => {
        const selectedOptionRadioName = getSelectedRadioOptionName(name);
        setSelectedRadioOption({
            ...selectedRadioOption,
            [selectedOptionRadioName]: value,
        });
    };

    useEffect(() => {
        if (currentFilters !== undefined) {
            switch (facetName) {
                case HSearchFilters.CONTENTTYPE:
                    if (currentFilters.contentType) {
                        handleSelectedRadioOption(
                            HSearchFilters.CONTENTTYPE,
                            currentFilters.contentType
                        );
                    }
                    break;
                case HSearchFilters.AGESPECIFIC: {
                    if (currentFilters.ageSpecific) {
                        const ageNumber = Number(
                            currentFilters.ageSpecific.slice(-1)
                        ) as keyof typeof AgeFilterTitles;
                        const ageTitleArray = AgeFilterTitles[ageNumber];
                        if (ageTitleArray) {
                            const name = ageTitleArray[0];
                            handleSelectedRadioOption(
                                HSearchFilters.AGESPECIFIC,
                                name
                            );
                        }
                    }
                    break;
                }
                case HSearchFilters.SPECIALITIES:
                    if (currentFilters.speciality) {
                        handleSelectedRadioOption(
                            HSearchFilters.SPECIALITIES,
                            trim(currentFilters.speciality)
                        );
                    }
                    break;
                case HSearchFilters.CATEGORIES:
                    if (currentFilters.category) {
                        handleSelectedRadioOption(
                            HSearchFilters.CATEGORIES,
                            trim(currentFilters.category)
                        );
                    }
                    break;
                case HSearchFilters.PATIENTEDUCATIONTYPE: {
                    if (currentFilters.subType) {
                        const subTypeValue = trim(currentFilters.subType);
                        handleSelectedRadioOption(
                            HSearchFilters.PATIENTEDUCATIONTYPE,
                            subTypeValue
                        );
                    }
                    break;
                }
                default:
                    break;
            }
        }
    }, [currentFilters]);

    useEffect(() => {
        if (currentFilters !== undefined) {
            setSelectedRadioOption({
                contentType: currentFilters.contentType,
                ageSpecific: currentFilters.ageSpecific,
                subType: currentFilters.subType,
                speciality: currentFilters.speciality,
                category: currentFilters.category,
            });
        }
    }, [currentFilters]);

    // Handle AGE SPECIFIC filter: Transform backend data using AgeFilterTitles
    useEffect(() => {
        if (facetData?.facetName !== HSearchFilters.AGESPECIFIC) return;

        const dataMap = new Map(
            facetData?.facetValues?.map(
                ({ name, count }: { name: string; count: number }) => [
                    name,
                    count,
                ]
            )
        );

        const newData = Object.values(AgeFilterTitles).map((name) => ({
            name: name[0],
            count: Number(dataMap.get(name[0]) || 0),
        }));
        setFacetValueData(newData);
    }, [facetData]);

    // Process and display SPECIALITIES and CATEGORIES filter data with alphabetical order
    useEffect(() => {
        if (!isOrderPreservedFilter) return;

        // Always sort alphabetically to maintain consistent order
        const sortedFacetValues = sortFacetValuesAlphabetically(
            facetData?.facetValues
        );

        // Determine which items to display based on show more/less state
        const facetValue =
            facetValueRows > 5 && !isShowMoreOpen
                ? sortedFacetValues?.slice(0, 5)
                : sortedFacetValues;

        setFacetValueData(facetValue);
        setFiltertext('');
        setFilterApplied(false);

        // Update show more/less button text based on current state
        setTitleShowMoreLess(
            isShowMoreOpen
                ? `Show Less (${defaultVisibleItems})`
                : `Show More (${facetValueRows - defaultVisibleItems})`
        );
    }, [
        facetData,
        isOrderPreservedFilter,
        facetName,
        facetValueRows,
        isShowMoreOpen,
    ]);

    // Handle standard filters (non-AGE, non-SPECIALITIES, non-CATEGORIES)
    useEffect(() => {
        if (
            facetData?.facetName === HSearchFilters.AGESPECIFIC ||
            isOrderPreservedFilter
        ) {
            return;
        }

        const facetValue =
            facetValueRows > 5 && !isShowMoreOpen
                ? facetData?.facetValues?.slice(0, 5)
                : facetData?.facetValues;

        setFacetValueData(facetValue);
        setFiltertext('');
        setFilterApplied(false);
    }, [facetData, isShowMoreOpen, isOrderPreservedFilter, facetValueRows]);

    // Focus on 6th item when Show More is expanded (all filters with Show More)
    useEffect(() => {
        if (shouldFocusSixthItem && isShowMoreOpen && containerRef.current) {
            setTimeout(() => {
                // Find all radio inputs within this specific container using ref
                const radioInputs = containerRef.current?.querySelectorAll(
                    'input[type="radio"]'
                );
                if (radioInputs && radioInputs.length > 5) {
                    (radioInputs[5] as HTMLInputElement).focus();
                }
                setShouldFocusSixthItem(false);
            }, 100);
        }
    }, [shouldFocusSixthItem, isShowMoreOpen]);

    // Focus on 1st item when Show Less is collapsed (all filters with Show More)
    useEffect(() => {
        if (shouldFocusFirstItem && !isShowMoreOpen && containerRef.current) {
            setTimeout(() => {
                // Find all radio inputs within this specific container using ref
                const radioInputs = containerRef.current?.querySelectorAll(
                    'input[type="radio"]'
                );
                if (radioInputs && radioInputs.length > 0) {
                    (radioInputs[0] as HTMLInputElement).focus();
                }
                setShouldFocusFirstItem(false);
            }, 100);
        }
    }, [shouldFocusFirstItem, isShowMoreOpen]);

    // Handle CONTENT TYPE filter initialization
    useEffect(() => {
        if (facetName === HSearchFilters.CONTENTTYPE) {
            handleSelectedRadioOption(
                HSearchFilters.CONTENTTYPE,
                selectedOptionContentType
            );
        }
    }, [selectedOptionContentType, facetName]);

    // Clear filter selections when currentFilters are reset
    useEffect(() => {
        if (currentFilters === undefined) return;

        const filterClearMap: Record<string, string> = {
            [HSearchFilters.AGESPECIFIC]: currentFilters.ageSpecific,
            [HSearchFilters.SPECIALITIES]: currentFilters.speciality,
            [HSearchFilters.CATEGORIES]: currentFilters.category,
            [HSearchFilters.PATIENTEDUCATIONTYPE]: currentFilters.subType,
        };

        const filterValue = filterClearMap[facetName];
        if (filterValue === '') {
            handleSelectedRadioOption(facetName, '');
        }
    }, [currentFilters, facetName]);

    useEffect(() => {
        setIsPanelOpen(isToggleOpen);
    }, [isToggleOpen]);

    const handleOnRadioChange = (facetName: string, optionName: any) => {
        onHandleChangeOption(facetName, optionName);
    };
    const handleOnShowMoreLess = () => {
        const isVisible = !isShowMoreOpen;
        setShowMoreOpen(isVisible);

        setTitleShowMoreLess(
            isVisible
                ? `Show Less (${defaultVisibleItems})`
                : `Show More (${facetValueRows - defaultVisibleItems})`
        );

        // Sort facet values alphabetically for SPECIALITIES and CATEGORIES filters
        if (isOrderPreservedFilter) {
            const sortedFacetValues = sortFacetValuesAlphabetically(
                facetData?.facetValues
            );

            setFacetValueData(
                isVisible ? sortedFacetValues : sortedFacetValues?.slice(0, 5)
            );
        } else {
            // Original behavior for other filters
            setFacetValueData(
                isVisible
                    ? facetData?.facetValues
                    : facetData?.facetValues?.slice(0, 5)
            );
        }

        // Trigger focus on 6th item when expanding, 1st item when collapsing
        if (isVisible) {
            setShouldFocusSixthItem(true);
        } else {
            setShouldFocusFirstItem(true);
        }

        onClickShowMoreLess(facetName, isVisible);
    };
    const handleTextInputChange = (value: string) => {
        const filterFacetValue = facetData?.facetValues?.filter((item: any) =>
            item.name.toLowerCase().includes(value.toLowerCase())
        );

        const filterDisplayName =
            HSearchFilterDisplayName[facetName] || facetName;

        if (value.length === 0) {
            setAnnouncementMessage(
                `Search input cleared. Showing all ${facetValueRows} ${filterDisplayName}.`
            );
        } else if (filterFacetValue?.length === 1) {
            setAnnouncementMessage(`${filterFacetValue.length} result found`);
        } else if (filterFacetValue?.length > 1) {
            setAnnouncementMessage(`${filterFacetValue.length} results found`);
        } else if (filterFacetValue?.length === 0) {
            setAnnouncementMessage('No results found');
        }

        const filterFacetValueRows =
            filterFacetValue?.length > 5 && !isShowMoreOpen
                ? filterFacetValue?.slice(0, 5)
                : filterFacetValue;

        setFacetValueData(filterFacetValueRows);
        setFilterApplied(value.length !== 0);
        setFiltertext(value);
    };

    const handleTogglePanel = () => {
        setIsPanelOpen(!isPanelOpen);
    };

    const headerTemplate = (
        <>
            {HSearchFilterDisplayName[facetName] || facetName}
            {(facetName === HSearchFilters.SPECIALITIES ||
                facetName === HSearchFilters.CATEGORIES) && (
                <Pill
                    className={`${baseClassName}__filter-section__card__content__pill`}
                    pillColor="n1"
                    textColor="text"
                >
                    {String(
                        isFilterApplied
                            ? facetValueData?.length ?? 0
                            : facetValueRows
                    )}
                </Pill>
            )}
        </>
    );

    const isRadioChecked = (facetName: string, facetVal: any) => {
        const selectedValue = getSelectedValue(facetName);
        if (facetName === HSearchFilters.CONTENTTYPE) {
            return selectedValue === facetVal?.name;
        }
        if (facetName === HSearchFilters.AGESPECIFIC) {
            if (!selectedValue) return false;
            const match = selectedValue.match(/\d+/);
            if (!match) return false;
            const ageNumber = Number(match[0]) as keyof typeof AgeFilterTitles;
            const displayName = AgeFilterTitles[ageNumber]?.[0];
            return displayName === facetVal?.name;
        }
        return (
            selectedValue === getFacetValueFromFacetData(facetName, facetVal)
        );
    };

    // useEffect to set aria-labels on radio inputs
    useEffect(() => {
        if (!radiogroupRef.current) return;

        const radioInputs = radiogroupRef.current.querySelectorAll(
            'input[type="radio"]'
        );

        radioInputs.forEach((input, index) => {
            const htmlInput = input as HTMLInputElement;
            const facetVal = facetValueData?.[index];

            if (facetVal) {
                const countText =
                    !NumberNotDisplayTitles.includes(facetName) &&
                    facetVal.count !== undefined &&
                    facetVal.count !== null
                        ? `${facetVal.count} results`
                        : '';
                const ariaLabel = `${facetVal.name} ${countText}`;
                htmlInput.setAttribute('aria-label', ariaLabel.trim());
            }
        });
    }, [facetValueData, facetName]);

    return (
        <div
            className={`${baseClassName}__filter-section__card__content`}
            ref={containerRef}
        >
            <TogglePanel
                header={headerTemplate}
                isOpen={isPanelOpen}
                isControlled
                className={`${baseClassName}__filter-section__card__content__type`}
                onClick={handleTogglePanel}
                headerLevel="3"
            >
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                    className={`${baseClassName}__filter-section__card__content__type__section-wrapper`}
                >
                    <div
                        className={`${baseClassName}__filter-section__card__content__type__focus-target`}
                        tabIndex={facetValueRows > 5 ? 0 : -1}
                        id="content-type-select"
                        aria-label={`${HSearchFilterDisplayName[facetName]} filter section`}
                    />
                    {facetValueRows > 5 && (
                        <>
                            <TextInput
                                placeholder={`Search ${
                                    HSearchFilterDisplayName[facetName] ||
                                    facetName
                                }`}
                                label=""
                                aria-label={`Search ${
                                    HSearchFilterDisplayName[facetName] ||
                                    facetName
                                }`}
                                aria-describedby={`${facetName}-Help-Text`}
                                iconRight={{
                                    sprite: 'Search',
                                }}
                                className={`${baseClassName}__filter-section__card__content__type__search`}
                                onChange={(e: any) => {
                                    handleTextInputChange(e.target.value);
                                }}
                                value={filterText}
                            />
                            <span
                                id={`${facetName}-Help-Text`}
                                className="u-els-hide-visually"
                            >
                                Results will update below automatically as you
                                type
                            </span>
                            <AriaLiveAnnouncer message={announcementMessage} />
                        </>
                    )}
                    <div
                        ref={radiogroupRef}
                        role="group"
                        aria-label={`${HSearchFilterDisplayName[facetName]} filter options, ${facetData?.facetValues?.length} results`}
                        className={`${baseClassName}__filter-section__card__content__type__section-wrapper`}
                    >
                        {facetValueData?.length > 0 ? (
                            <>
                                {facetValueData?.map((facetVal: any) => (
                                    <RadioButtonGroup.Option
                                        key={`op-${facetVal?.name}`}
                                        name={facetName}
                                        label={`${facetVal?.name} ${
                                            NumberNotDisplayTitles.includes(
                                                facetName
                                            )
                                                ? ''
                                                : `(${facetVal?.count})`
                                        }`}
                                        value={facetVal?.name}
                                        onChange={
                                            () =>
                                                handleOnRadioChange(
                                                    facetData?.facetName,
                                                    facetVal.name
                                                )
                                            // eslint-disable-next-line react/jsx-curly-newline
                                        }
                                        checked={isRadioChecked(
                                            facetName,
                                            facetVal
                                        )}
                                        className="__item"
                                    />
                                ))}

                                {!isFilterApplied &&
                                    facetValueRows > defaultVisibleItems && (
                                        <TogglePanel
                                            header={
                                                <div
                                                    aria-label={`${titleShowMoreLess} ${
                                                        isSpecialitiesFilter
                                                            ? 'Specialities'
                                                            : 'Categories'
                                                    }`}
                                                >
                                                    {titleShowMoreLess}
                                                </div>
                                            }
                                            isOpen={isShowMoreOpen}
                                            headerLevel="4"
                                            className={`${baseClassName}__filter-section__card__content__show-more-less`}
                                            onClick={handleOnShowMoreLess}
                                            size="s"
                                        >
                                            <span>Test</span>
                                        </TogglePanel>
                                    )}
                            </>
                        ) : (
                            <div
                                className={`${
                                    facetValueData?.length === 0
                                        ? 'no-filter-search-result no-image-result'
                                        : 'no-filter-search-result'
                                }`}
                            >
                                <NoSearchResults
                                    mainText="No results match this search."
                                    countValue={facetData?.facetValues?.length}
                                    forFilterPanel
                                />
                            </div>
                        )}
                    </div>
                </div>
            </TogglePanel>
        </div>
    );
};

export default CardFilterPanel;
